import React, {useState} from 'react';
import VideoPlayerControls from '../../../uiToolkit/VideoPlayerControls';
import {pauseControlsIcon, playControlsIcon} from '../../../assets/images';
import PlayerFullscreenButton from '../../../uiToolkit/Buttons/PlayerFullscreenButton/PlayerFullscreenButton';
import {LessonContentVideo} from '../../../data/models/LessonContent/LessonContentBase';
import {StartEndPlayerRef} from '../models';
import StartEndPlayer from './StartEndPlayer';
import {truncateDurationTo3Decimals} from '../../../utils/getTimeTextFromSeconds';

const RATIO_16_9 = 1.7777;

const MILISECONDS_OVERFLOW_MARGIN = 0.03;
const MILISECONDS_IMAGE_OVERFLOW_MARGIN = 0.5;

interface Props {
  onFinished: () => void;
  currentNode?: LessonContentVideo;
  started: boolean;
  showControls?: boolean;
  fitHeight?: boolean;
  onWidthCalculated: (width: number) => void;
  reference?: (ref: any) => void;
  setProgres: (prog: number) => void;
  hideSmallControls?: boolean;
  onFullscreen?: () => void;
}

const PreviewVideoPlayer = React.memo(
  ({
    onFinished,
    currentNode,
    started,
    showControls,
    fitHeight,
    onWidthCalculated,
    reference,
    setProgres,
    hideSmallControls,
    onFullscreen,
  }: Props) => {
    const [firstPlayerSource, setFirstPlaterSource] =
      useState<LessonContentVideo>();
    const [secondPlayerSource, setSecondPlayerSource] =
      useState<LessonContentVideo>();
    const [activeFirstPlayer, setActiveFirstPlayer] = useState(true);
    const [playerLoaded, setPlayerLoaded] = useState(false);
    const [width, setWidth] = React.useState(0);
    const [height, setHeight] = React.useState(0);
    const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);

    const [currentPlayerRef, setCurrentPlayerRef] =
      useState<StartEndPlayerRef>();

    const containerRef = React.useRef<any>(null);

    React.useEffect(() => {
      if (reference) {
        reference({pause: refPause, seekTo: refSeek});
      }
    }, [currentPlayerRef]);

    React.useEffect(() => {
      if (currentPlayerRef) {
        setProgres(currentPlayerRef.currentTime);
      }
    }, [currentPlayerRef]);

    const widthReady = width > 0;

    React.useEffect(() => {
      if (currentNode && widthReady) {
        if (activeFirstPlayer) {
          setSecondPlayerSource(currentNode);
        } else {
          setFirstPlaterSource(currentNode);
        }
      }
    }, [currentNode, widthReady]);

    const onCurrentPlayerFinished = () => {
      onFinished();
    };

    React.useEffect(() => {
      const onFullscreenChange = () => {
        setTimeout(setPlayerDimentions, 100);
      };
      window.addEventListener('resize', setPlayerDimentions);
      document.addEventListener('fullscreenchange', onFullscreenChange);
      return () => {
        window.removeEventListener('resize', setPlayerDimentions);
        document.removeEventListener('fullscreenchange', onFullscreenChange);
      };
    }, []);

    React.useLayoutEffect(() => {
      setPlayerDimentions();
    }, []);

    React.useEffect(() => {
      if (width !== 0) {
        onWidthCalculated(getVideoWidth());
      }
    }, [width]);

    const setPlayerDimentions = () => {
      if (containerRef && containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
        setHeight(containerRef.current.offsetHeight);
      }
    };

    const onFirstPlayerLoaded = () => {
      setActiveFirstPlayer(true);
      setSecondPlayerSource(undefined);
      setPlayerLoaded(true);
    };

    const onSecondPlayerLoaded = () => {
      setActiveFirstPlayer(false);
      setFirstPlaterSource(undefined);
      setPlayerLoaded(true);
    };

    const refPause = () => {
      currentPlayerRef?.setPlaying(false);
    };

    const refSeek = (seconds: number) => {
      currentPlayerRef?.seekTo(seconds);
    };

    const getEndTime = (source?: LessonContentVideo) => {
      if (source) {
        if (source.generatedFromImage) {
          return source.endInSeconds - MILISECONDS_IMAGE_OVERFLOW_MARGIN;
        } else {
          return source.endInSeconds - MILISECONDS_OVERFLOW_MARGIN;
        }
      }
      return 1;
    };

    const getVideoHeight = () => {
      if (fitHeight) {
        if (window.innerHeight < height) {
          return window.innerHeight;
        }
        return height;
      } else {
        return width / RATIO_16_9;
      }
    };

    const getVideoWidth = () => {
      if (fitHeight) {
        if (getVideoHeight() === window.innerHeight) {
          return (16 * window.innerHeight) / 9;
        } else {
          return width;
        }
      } else {
        return width;
      }
    };

    const clipStart = currentNode
      ? truncateDurationTo3Decimals(currentNode.startInSeconds)
      : 0;
    const clipEnd = getEndTime(currentNode);
    const clipDuration = clipEnd - clipStart;

    const subtitlesUrl = currentNode?.subtitlesUrl ?? '';
    const subtitlesAvailable: boolean = !!subtitlesUrl && subtitlesUrl !== '';

    return (
      <div className="VideoPlaybackPlayerContainer" ref={containerRef}>
        <div className="VideoCover" />
        {(!started || !playerLoaded) && (
          <div
            style={{
              width: width,
              height: width / RATIO_16_9,
            }}
          />
        )}
        <StartEndPlayer
          source={firstPlayerSource?.contentUrl || ''}
          subtitlesUrl={subtitlesUrl}
          subtitlesAvailable={subtitlesAvailable}
          subtitlesEnabled={subtitlesEnabled}
          startTime={
            firstPlayerSource
              ? truncateDurationTo3Decimals(firstPlayerSource.startInSeconds)
              : 0
          }
          endTime={getEndTime(firstPlayerSource)}
          width={getVideoWidth()}
          visible={started && activeFirstPlayer}
          height={getVideoHeight()}
          onFinished={onCurrentPlayerFinished}
          reference={activeFirstPlayer ? setCurrentPlayerRef : undefined}
          onLoaded={onFirstPlayerLoaded}
          id="FIRST"
        />
        <StartEndPlayer
          source={secondPlayerSource?.contentUrl || ''}
          subtitlesUrl={subtitlesUrl}
          subtitlesAvailable={subtitlesAvailable}
          subtitlesEnabled={subtitlesEnabled}
          startTime={
            secondPlayerSource
              ? truncateDurationTo3Decimals(secondPlayerSource.startInSeconds)
              : 0
          }
          endTime={getEndTime(secondPlayerSource)}
          width={getVideoWidth()}
          visible={started && !activeFirstPlayer}
          height={getVideoHeight()}
          onFinished={onCurrentPlayerFinished}
          reference={!activeFirstPlayer ? setCurrentPlayerRef : undefined}
          onLoaded={onSecondPlayerLoaded}
          id="SECOND"
        />
        {showControls ? (
          <VideoPlayerControls
            playing={currentPlayerRef?.isPlaying || false}
            duration={clipDuration}
            togglePlaying={currentPlayerRef?.togglePlaying}
            currentTime={
              (currentPlayerRef?.currentTime || clipStart) - clipStart
            }
            seekTo={
              currentPlayerRef?.seekTo
                ? (time: number) => currentPlayerRef.seekTo(time + clipStart)
                : undefined
            }
            setPlaying={currentPlayerRef?.setPlaying}
            subtitlesAvailable={subtitlesAvailable}
            subtitlesEnabled={subtitlesEnabled}
            setSubtitlesEnabled={setSubtitlesEnabled}
            onFullscreen={onFullscreen}
          />
        ) : !hideSmallControls ? (
          <div className="VideoPlaybackActions">
            <button
              className="SmallPlayButton"
              style={{top: `${getVideoHeight() - 40}px`}}
              onClick={currentPlayerRef?.togglePlaying}>
              <img
                src={
                  currentPlayerRef?.isPlaying
                    ? pauseControlsIcon
                    : playControlsIcon
                }
                alt="play"
                className="Icon"
              />
            </button>
            <PlayerFullscreenButton
              className="SmallFullscreenButton"
              onClick={onFullscreen}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

export default PreviewVideoPlayer;
